import React, { useEffect, useState } from "react";
import { Plus, UserPlus, Trash2, X as XIcon, Copy, Check } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Modal, PrimaryButton, GhostButton } from "../../components/shell";
import { useAuth } from "../../lib/auth";

const ROLE_OPTIONS = [
    { value: "club_admin",          label: "Club admin",         hint: "Full access — invite team, edit brand, Stripe, pricing, tiers." },
    { value: "club_editor",         label: "Club editor",        hint: "Sponsors, packages, players, pitch, enquiries. Cannot edit brand / pricing / Stripe." },
    { value: "commercial_director", label: "Commercial director", hint: "Same access as editor — labelled differently for the org chart." },
    { value: "matchday_admin",      label: "Matchday admin",     hint: "Same access as editor — matchday operations focus." },
];

const ROLE_LABEL = Object.fromEntries(ROLE_OPTIONS.map((r) => [r.value, r.label]));

export default function MembersTab({ tenant }) {
    const [members, setMembers] = useState([]);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [showInvite, setShowInvite] = useState(false);
    const [justInvited, setJustInvited] = useState(null); // { email, password }
    const { user } = useAuth();

    const load = async () => {
        setBusy(true);
        try {
            const { data } = await api.get(`/tenants/${tenant.id}/members`);
            setMembers(data);
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => { if (tenant?.id) load(); }, [tenant?.id]);

    const invite = async (draft) => {
        try {
            const { data } = await api.post(`/tenants/${tenant.id}/members`, draft);
            setJustInvited({ email: data.email, password: data._provisional_password });
            setShowInvite(false);
            load();
        } catch (e) {
            alert(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    const remove = async (m) => {
        if (!window.confirm(`Remove ${m.name || m.email} from the team? They lose access immediately.`)) return;
        try {
            await api.delete(`/tenants/${tenant.id}/members/${m.id}`);
            load();
        } catch (e) {
            alert(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    const canManage = user?.role === "platform_owner" || (user?.role === "club_admin" && user?.tenant_id === tenant.id);

    return (
        <div className="space-y-6" data-testid="members-tab">
            <div className="flex items-center justify-between gap-4">
                <div>
                    <div className="text-[10px] uppercase tracking-ultra-wide text-[var(--sb-amber)] mb-1">Access</div>
                    <h1 className="font-display font-extrabold text-2xl md:text-3xl tracking-tight">Team members</h1>
                    <p className="text-sm text-zinc-400 mt-1">
                        Give members of your commercial and matchday teams their own login. Roles set exactly what they can and can't touch.
                    </p>
                </div>
                {canManage && (
                    <PrimaryButton tenantTheme onClick={() => setShowInvite(true)} data-testid="members-invite-btn">
                        <UserPlus size={14} strokeWidth={2.4} /> Invite member
                    </PrimaryButton>
                )}
            </div>

            {err && <div className="border border-rose-500/40 text-rose-300 p-4 text-sm">{err}</div>}

            {justInvited && (
                <div className="border hairline p-5 bg-[rgba(255,215,0,0.03)]" data-testid="member-just-invited">
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                            <div className="text-[10px] uppercase tracking-ultra-wide text-[var(--sb-amber)] mb-1">Share these credentials once</div>
                            <div className="font-display font-bold text-lg tracking-tight">Invite ready for {justInvited.email}</div>
                            <div className="text-xs text-zinc-400 mt-1">One-time password. Ask them to sign in and rotate it in their profile.</div>
                            <div className="mt-3 flex flex-wrap items-center gap-3">
                                <CopyChip label="Email" value={justInvited.email} />
                                <CopyChip label="Password" value={justInvited.password} mono />
                            </div>
                        </div>
                        <button onClick={() => setJustInvited(null)} className="text-zinc-400 hover:text-white p-1"><XIcon size={14} /></button>
                    </div>
                </div>
            )}

            <section className="border hairline">
                <div className="hidden md:grid grid-cols-12 px-6 py-3 border-b hairline text-[10px] uppercase tracking-ultra-wide text-zinc-500">
                    <div className="col-span-4">Member</div>
                    <div className="col-span-3">Role</div>
                    <div className="col-span-3">Title</div>
                    <div className="col-span-2 text-right">Actions</div>
                </div>
                {busy && members.length === 0 && <div className="p-6 text-sm text-zinc-500">Loading team…</div>}
                {!busy && members.length === 0 && (
                    <div className="p-8 text-sm text-zinc-500 italic text-center">No team members yet. Invite your first colleague above.</div>
                )}
                {members.map((m, i) => (
                    <div key={m.id} className={`grid grid-cols-12 items-center px-6 py-4 text-sm ${i !== 0 ? "border-t hairline" : ""}`} data-testid={`member-row-${m.id}`}>
                        <div className="col-span-12 md:col-span-4">
                            <div className="text-white truncate">{m.name || m.email}</div>
                            <div className="text-[11px] text-zinc-500 truncate font-mono">{m.email}</div>
                        </div>
                        <div className="col-span-6 md:col-span-3">
                            <span className="inline-flex items-center border hairline px-2 py-0.5 text-[10px] uppercase tracking-ultra-wide"
                                style={m.role === "club_admin" ? { background: "var(--tenant-primary)", color: "#000", borderColor: "transparent" } : {}}
                            >{ROLE_LABEL[m.role] || m.role}</span>
                        </div>
                        <div className="col-span-6 md:col-span-3 text-zinc-400 truncate">{m.title || "—"}</div>
                        <div className="col-span-12 md:col-span-2 flex justify-end">
                            {canManage && m.id !== user?.id && (
                                <button onClick={() => remove(m)} className="text-rose-400 hover:text-rose-300 p-1.5" data-testid={`member-remove-${m.id}`} aria-label="Remove"><Trash2 size={13} /></button>
                            )}
                            {m.id === user?.id && <span className="text-[10px] text-zinc-500 uppercase tracking-ultra-wide">You</span>}
                        </div>
                    </div>
                ))}
            </section>

            {showInvite && <InviteModal onClose={() => setShowInvite(false)} onSubmit={invite} />}
        </div>
    );
}


const InviteModal = ({ onClose, onSubmit }) => {
    const [draft, setDraft] = useState({ email: "", name: "", role: "club_editor", title: "" });
    const submit = (e) => {
        e.preventDefault();
        if (!draft.email.trim() || !draft.name.trim()) return alert("Email and name required");
        onSubmit({ ...draft, email: draft.email.trim().toLowerCase(), name: draft.name.trim() });
    };
    const roleMeta = ROLE_OPTIONS.find((r) => r.value === draft.role);
    return (
        <Modal open onClose={onClose} title="Invite team member" testId="member-invite-modal">
            <form onSubmit={submit} className="space-y-4">
                <div>
                    <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-1">Full name *</div>
                    <input required value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                        className="w-full bg-[#060b07] border hairline px-3 py-2 text-sm text-white outline-none focus:border-white/30"
                        placeholder="Jane Doe" data-testid="member-name" />
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-1">Work email *</div>
                    <input required type="email" value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })}
                        className="w-full bg-[#060b07] border hairline px-3 py-2 text-sm text-white outline-none focus:border-white/30"
                        placeholder="jane@yourclub.co.uk" data-testid="member-email" />
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-1">Role *</div>
                    <select value={draft.role} onChange={(e) => setDraft({ ...draft, role: e.target.value })}
                        className="w-full bg-[#060b07] border hairline px-3 py-2 text-sm text-white outline-none focus:border-white/30"
                        data-testid="member-role">
                        {ROLE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                    {roleMeta && <div className="text-[10px] italic text-zinc-500 mt-1 leading-relaxed">{roleMeta.hint}</div>}
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-1">Job title</div>
                    <input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                        className="w-full bg-[#060b07] border hairline px-3 py-2 text-sm text-white outline-none focus:border-white/30"
                        placeholder="Commercial Manager" data-testid="member-title" />
                </div>
                <div className="text-[11px] text-zinc-500 italic border-t hairline pt-3">
                    We&apos;ll generate a one-time password for them. Share it with them via a secure channel — they can rotate it after their first sign-in.
                </div>
                <div className="flex items-center justify-end gap-2">
                    <GhostButton type="button" onClick={onClose}><XIcon size={14} /> Cancel</GhostButton>
                    <PrimaryButton tenantTheme type="submit" data-testid="member-invite-submit">
                        <Plus size={14} strokeWidth={2.4} /> Send invite
                    </PrimaryButton>
                </div>
            </form>
        </Modal>
    );
};

const CopyChip = ({ label, value, mono }) => {
    const [copied, setCopied] = useState(false);
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch { /* clipboard blocked */ }
    };
    return (
        <button onClick={copy} className="inline-flex items-center gap-2 border hairline px-3 py-1.5 text-xs hover:border-white/30" type="button" data-testid={`copy-${label.toLowerCase()}`}>
            <span className="text-[10px] uppercase tracking-ultra-wide text-zinc-500">{label}</span>
            <span className={`text-white ${mono ? "font-mono" : ""}`}>{value}</span>
            {copied ? <Check size={12} className="text-emerald-300" /> : <Copy size={12} className="text-zinc-400" />}
        </button>
    );
};
